<template>
  <v-sheet>
    <v-card-text>
      <v-scale-transition
        group
        hide-on-leave
      >
        <v-form
          v-if="isLoginFormVisible"
          ref="loginForm"
          key="login-form"
          v-model="isLoginFormValid"
        >
          <p
            v-if="!isSessionInactive"
            class="mb-8 text--secondary text-body-1"
          >
            Please sign-in to your account
          </p>

          <v-row
            v-if="isSessionInactive"
            no-gutters
          >
            <v-col
              cols="12"
            >
              <p>
                Sign in as {{ currentUser.login }}.
                <a
                  class="blue--text"
                  @click.prevent="_ => handleLoginDifferentUser()"
                >
                  <strong>Not you?</strong>
                </a>
              </p>
            </v-col>
          </v-row>

          <v-text-field
            v-else
            v-model="username"
            autofocus
            label="Username or e-mail"
            prepend-inner-icon="mdi-account"
            outlined
            :rules="rules.username"
            @keydown.enter.prevent="_ => onSubmitForm()"
          />

          <PasswordField
            v-model="password"
            hide-details="auto"
            label="Password"
            outlined
            prepend-inner-icon="mdi-key"
            :rules="rules.password"
            @keydown.enter.prevent="_ => onSubmitForm()"
          />

          <div
            class="d-flex"
          >
            <a
              class="d-block link--text ms-1 mt-2"
              @click="openAccountRecovery"
            >
              Forgot Password?
            </a>

            <v-spacer />

            <a
              v-if="!isSessionInactive"
              class="black--text d-block link--text me-1 mt-2 text-decoration-none"
              @click="_ => openAccountVerification()"
            >
              Verify Account
            </a>
          </div>

          <template
            v-if="isMultifactorAuthenticationActive"
          >
            <v-row
              align="center"
              class="align-content-center my-5"
            >
              <v-divider />
              <span class="lighten-3 mx-4 text-caption text-uppercase">Select an Authentication Method</span>
              <v-divider />
            </v-row>
            <div
              class="text-center"
            >
              <v-btn
                v-if="isAuthenticationByMagicLinkEnabled"
                color="primary"
                class="mb-3"
                block
                @click="requestVerification('magiclink')"
              >
                <v-icon left>
                  mdi-email-lock
                </v-icon>
                Email me a magic link
              </v-btn>

              <v-btn
                v-if="isAuthenticationByOTPViaEmailEnabled"
                class="mb-3"
                block
                text
                outlined
                @click="requestVerification('otpbymail')"
              >
                <v-icon left>
                  mdi-email-lock
                </v-icon>
                Email me a one-time code
              </v-btn>

              <v-btn
                v-if="isAuthenticationByOTPViaTextMessageEnabled"
                class="mb-3"
                block
                text
                outlined
                @click="requestVerification('otpbytext')"
              >
                <v-icon left>
                  mdi-message-lock
                </v-icon>
                OTP via text message
              </v-btn>

              <v-btn
                v-if="isAuthenticationByTOTPEnabled"
                class="mb-3"
                block
                text
                outlined
                @click="requestVerification('totp')"
              >
                <v-icon left>
                  mdi-shield-lock
                </v-icon>
                Authenticator app
              </v-btn>
            </div>
          </template>

          <template
            v-else
          >
            <div
              class="align-content-center my-5"
            >
              <v-btn
                color="primary"
                block
                @click.prevent="_ => onSubmitForm()"
              >
                <v-icon left>
                  mdi-lock-open
                </v-icon>
                Login
              </v-btn>
            </div>
          </template>
        </v-form>

        <div
          v-if="isLoadingScreenVisible"
          key="loading-screen"
          class="align-center d-flex flex-column py-4"
        >
          <v-progress-circular
            :size="70"
            :width="7"
            color="primary"
            indeterminate
          />

          <p
            class="mt-4 text-overline"
          >
            Please wait...
          </p>
        </div>

        <div
          v-if="isUnauthorizedScreenVisible"
          key="unauthorized-screen"
        >
          <v-divider
            class="mb-5"
          />

          <p>We weren't able to verify your identity!</p>

          <p>Please try again and make sure to supply the correct username and password, and the second-factor authentication passcode.</p>

          <p>If this situation persists, please contact your administrator.</p>

          <p>Thank you</p>

          <div
            class="mt-4 text-right"
          >
            <v-btn
              outlined
              @click="showLoginForm"
            >
              <v-icon
                left
              >
                mdi-arrow-left
              </v-icon>
              Go back to the login screen
            </v-btn>
          </div>
        </div>

        <div
          v-if="isMagicLinkScreenVisible"
          key="magic-link-sent-screen"
        >
          <v-divider
            class="mb-5"
          />
          <p>A Magic Link has been sent to the e-mail address associated with your account.</p>
          <p>Please open that link in this browser, without closing it, in order to finish the authentication process.</p>
          <p>Thank you.</p>
        </div>

        <v-form
          v-if="isPasscodeScreenVisible"
          key="passcode-screen"
          @submit.prevent
        >
          <template
            v-if="isTimeBasedOneTimePassword"
          >
            <p>Please insert bellow the code generated by the authentication app you associated with your account.</p>
          </template>

          <template
            v-else
          >
            <p>A One-Time Password was sent to the e-mail address associated with your account.</p>
            <p>Please insert it bellow to complete your authentication.</p>
          </template>

          <v-otp-input
            ref="otpInput"
            v-model="passcode"
            class="mx-12"
            length="6"
            @finish="verifyPasscode"
          />

          <div
            class="d-flex mt-3"
          >
            <v-btn
              text
              @click="cancelVerification"
            >
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-btn>

            <v-spacer />

            <v-btn
              text
              @click="verifyPasscode"
            >
              <v-icon
                left
              >
                mdi-send
              </v-icon>
              Login
            </v-btn>
          </div>
        </v-form>

        <div
          v-if="isErrorScreenVisible"
          key="error-screen"
        >
          <v-divider
            class="mb-5"
          />
          <p>There was an error while making the request.</p>
          <template
            v-if="errors.length"
          >
            <p
              v-for="(message, index) of errors"
              :key="index"
            >
              {{ message }}
            </p>
          </template>
          <template v-else-if="errorMessage">
            <p>{{ errorMessage }}</p>
          </template>
          <template
            v-else
          >
            <p>Please try again, and if the issue persist contact your administrator.</p>
          </template>
          <p>Thank you.</p>
          <div
            class="mt-4 text-right"
          >
            <v-btn
              outlined
              @click="showLoginForm"
            >
              <v-icon
                left
              >
                mdi-arrow-left
              </v-icon>
              Go back to the login screen
            </v-btn>
          </div>
        </div>
      </v-scale-transition>
    </v-card-text>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { AUTHENTICATION_NAMESPACE } from '@/store/authentication'
import PasswordField from '../Misc/PasswordField'
import { alertHandler } from '@/alertHandler'
import { rules } from '@/utilities/validation-rules'

const Screens = {
  ErrorScreen: 6,
  LoadingScreen: 2,
  LoginForm: 1,
  MagicLinkScreen: 3,
  PasscodeScreen: 4,
  UnauthorizedScreen: 5,
}

export default {
  name: 'LoginForm',

  components: {
    PasswordField,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      currentScreen: Screens.LoadingScreen,
      errorMessage: '',
      errors: [],
      isLoginFormValid: undefined,
      passcode: '',
      password: '',
      rules: {
        password: [
          rules.required,
        ],

        username: [
          rules.required,
        ],
      },

      strategy: '',
      username: '',
    }
  },

  computed: {
    isErrorScreenVisible () {
      return this.currentScreen === Screens.ErrorScreen
    },

    isLoadingScreenVisible () {
      return this.currentScreen === Screens.LoadingScreen
    },

    isLoginFormVisible () {
      return this.currentScreen === Screens.LoginForm
    },

    isMagicLinkScreenVisible () {
      return this.currentScreen === Screens.MagicLinkScreen
    },

    isMultifactorAuthenticationActive () {
      return this.isMultifactorAuthenticationEnabled && (
        this.isAuthenticationByMagicLinkEnabled ||
        this.isAuthenticationByOTPViaEmailEnabled ||
        this.isAuthenticationByOTPViaTextMessageEnabled ||
        this.isAuthenticationByTOTPEnabled
      )
    },

    isPasscodeScreenVisible () {
      return this.currentScreen === Screens.PasscodeScreen
    },

    isTimeBasedOneTimePassword () {
      return this.strategy === 'totp'
    },

    isUnauthorizedScreenVisible () {
      return this.currentScreen === Screens.UnauthorizedScreen
    },

    ...mapGetters([
      'currentUser',
      'isSessionInactive',
      'sessionReturnTo',
    ]),

    ...mapGetters(AUTHENTICATION_NAMESPACE, [
      'isAuthenticationByMagicLinkEnabled',
      'isAuthenticationByOTPViaEmailEnabled',
      'isAuthenticationByOTPViaTextMessageEnabled',
      'isAuthenticationByTOTPEnabled',
      'isMultifactorAuthenticationEnabled',
    ]),
  },

  created () {
    // if we have a passcode on the url we try to authenticate
    if (this.$route.query.passcode) {
      this.passcode = this.$route.query.passcode
      this.strategy = 'magiclink'
      this.verifyPasscode()
    } else {
      this.showLoginForm()
    }
  },

  methods: {
    cancelVerification () {
      this.showLoginForm()
    },

    handleLoginDifferentUser () {
      this.$emit('on-close-auth-dialog')
      this.$router.push('/login')
      window.location.reload(true)
    },

    onAuthenticated () {
      // signal other tabs that authenication has succeeded
      window.localStorage.setItem('login', Date.now())

      if (this.isSessionInactive) {
        this.currentScreen = Screens.LoginForm
        this.$emit('on-close-auth-dialog')
      } else {
        // return user to the page they were on, if one is stored
        const path = this.sessionReturnTo

        // note: just catching here to avoid error in browser console. could do on all $router.push()
        if (path && !path.includes('/login') && this.$route.path !== path) this.$router.push(path).catch(_ => {})
        else if (this.$route.path !== '/') this.$router.push('/')
      }
    },

    onSubmitForm () {
      if (this.isMultifactorAuthenticationActive) return
      this.requestVerification('none')
    },

    openAccountRecovery () {
      this.$emit('on-open-account-recovery')
    },

    openAccountVerification () {
      this.$emit('on-open-account-verification')
    },

    requestVerification (strategy) {
      this.$refs.loginForm.validate()
      if (!this.isLoginFormValid) return

      this.showLoadingScreen()
      this.strategy = strategy
      this.requestAuthentication({
        password: this.password,
        redirectTo: this.sessionReturnTo,
        strategy: this.strategy,
        username: this.username,
      }).
        then(response => {
          if (this.strategy === 'none') {
            if (response?.user?.reset_required) {
              this.$router.push(`/login?rtoken=${response.user.reset_token}`)
              window.location.reload(true)
            } else this.onAuthenticated()
          } else if (this.strategy === 'magiclink') this.showMagicLinkScreen()
          else this.showPasscodeScreen()
        }).
        catch(response => {
          this.errors = response?.data?.errors || []
          this.errorMessage = response?.data?.message || ''
          this.showErrorScreen()
        })
    },

    resetFormData () {
      this.username = ''
      this.password = ''
      this.passcode = ''
    },

    showErrorScreen () {
      this.currentScreen = Screens.ErrorScreen
    },

    showLoadingScreen () {
      this.currentScreen = Screens.LoadingScreen
    },

    showLoginForm () {
      this.resetFormData()
      this.currentScreen = Screens.LoginForm

      if (this.isSessionInactive) this.username = this.currentUser.login
      else if (Object.keys(this.$route.query)?.length) {
        this.$router.push({ path: this.$route.path, query: {} })
      }
    },

    showMagicLinkScreen () {
      this.currentScreen = Screens.MagicLinkScreen
    },

    async showPasscodeScreen () {
      this.passcode = ''
      this.currentScreen = Screens.PasscodeScreen

      await this.$nextTick()
      this.$refs.otpInput?.focus()
    },

    showUnauthorizedScreen () {
      this.currentScreen = Screens.UnauthorizedScreen
    },

    verifyPasscode () {
      this.showLoadingScreen()

      this.verifyAuthenticationRequest({ passcode: this.passcode }).
        then(response => {
          // A response here would be a reset_url, so re-route to that
          if (response?.user?.reset_required) {
            this.$router.push(`/login?rtoken=${response.user.reset_token}`)
            window.location.reload(true)
          } else this.onAuthenticated()
        }).
        catch(error => {
          if (error.response?.status !== 401) this.showErrorScreen()
          else if (this.isTimeBasedOneTimePassword) {
            this.displayAlert('error', 'Invalid Passcode!')
            this.showPasscodeScreen()
          } else this.showUnauthorizedScreen()
        })
    },

    ...mapActions(AUTHENTICATION_NAMESPACE, [
      'requestAuthentication',
      'verifyAuthenticationRequest',
    ]),
  },
}
</script>
